/**
 * MessagesPage — Slack-inspired messaging hub
 *
 * Features:
 * - Channel sidebar with DMs, project channels, public channels
 * - Real-time message display with reactions and threading
 * - Message composer with @ mentions and emoji
 * - Typing indicators
 * - Unread badges per channel
 * - Create new channel / start DM
 * - Thread slide-out panel
 * - Mobile: channel list → conversation (drill-down)
 * - Search across messages
 *
 * Backend: messaging_api.py endpoints at /messaging/*
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Hash, Lock, MessageCircle, Users, Plus, Search, Send, Smile,
  ArrowLeft, MoreHorizontal, Pin, Trash2, Edit3, Reply, X,
  ChevronRight, ChevronDown, UserPlus, Settings, AtSign,
  Paperclip, Image as ImageIcon, Code, Star, Bell, BellOff,
  LogOut, Check, Loader2, MessageSquare, Sparkles, Volume2
} from 'lucide-react';
import { cn } from '../utils/cn';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const EMOJI_QUICK = ['👍', '❤️', '😂', '🎉', '🔥', '👀', '💡', '✅'];

const CHANNEL_ICONS = {
  public: Hash,
  private: Lock,
  direct_message: MessageCircle,
  group_dm: Users,
  project: Sparkles,
  study_group: Users,
};

// ============================================================================
// TIME HELPERS
// ============================================================================

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const seconds = Math.floor((now - d) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatMessageTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const formatDateDivider = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const shouldShowDateDivider = (currentMsg, prevMsg) => {
  if (!prevMsg) return true;
  const curr = new Date(currentMsg.created_at).toDateString();
  const prev = new Date(prevMsg.created_at).toDateString();
  return curr !== prev;
};

const shouldGroupMessages = (currentMsg, prevMsg) => {
  if (!prevMsg) return false;
  if (currentMsg.user_id !== prevMsg.user_id) return false;
  const diff = new Date(currentMsg.created_at) - new Date(prevMsg.created_at);
  return diff < 300000; // 5 minutes
};

// ============================================================================
// MINI AVATAR
// ============================================================================

const MiniAvatar = ({ src, username, size = 32, className }) => {
  const initials = (username || '?').charAt(0).toUpperCase();
  return (
    <div
      className={cn(
        'rounded-full overflow-hidden bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0',
        className
      )}
      style={{ width: size, height: size }}
    >
      {src ? (
        <img src={src} alt={username} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
      ) : (
        <span className="text-white font-semibold" style={{ fontSize: size * 0.4 }}>{initials}</span>
      )}
    </div>
  );
};

// ============================================================================
// CHANNEL LIST ITEM
// ============================================================================

const ChannelItem = ({ channel, isActive, onClick, currentUserId }) => {
  const Icon = CHANNEL_ICONS[channel.channel_type] || Hash;
  const isDM = channel.channel_type === 'direct_message' || channel.channel_type === 'group_dm';

  // For DMs, show the other person's name
  const displayName = isDM
    ? (channel.dm_user?.username || channel.display_name || channel.name)
    : (channel.display_name || channel.name);

  return (
    <button
      onClick={() => onClick(channel)}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all duration-100',
        'group',
        isActive
          ? 'bg-purple-500/15 text-white'
          : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/40'
      )}
    >
      {isDM ? (
        <MiniAvatar
          src={channel.dm_user?.profilePictureUrl}
          username={displayName}
          size={24}
        />
      ) : (
        <Icon className={cn('w-4 h-4 flex-shrink-0', isActive ? 'text-purple-400' : 'text-zinc-500')} />
      )}

      <span className={cn(
        'text-sm truncate flex-1',
        (channel.unread_count > 0 && !isActive) && 'font-semibold text-white'
      )}>
        {displayName}
      </span>

      {channel.unread_count > 0 && !isActive && (
        <span className="min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-purple-600 rounded-full">
          {channel.unread_count > 99 ? '99+' : channel.unread_count}
        </span>
      )}
    </button>
  );
};

// ============================================================================
// CHANNEL SIDEBAR
// ============================================================================

const ChannelSidebar = ({
  channels,
  activeChannelId,
  onSelectChannel,
  onCreateChannel,
  onCreateDM,
  currentUserId,
  loading,
  className,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    dms: true,
    channels: true,
    projects: true,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Group channels
  const grouped = useMemo(() => {
    const dms = [];
    const publicChannels = [];
    const projectChannels = [];

    channels.forEach(ch => {
      if (ch.channel_type === 'direct_message' || ch.channel_type === 'group_dm') {
        dms.push(ch);
      } else if (ch.channel_type === 'project' || ch.channel_type === 'study_group') {
        projectChannels.push(ch);
      } else {
        publicChannels.push(ch);
      }
    });

    return { dms, publicChannels, projectChannels };
  }, [channels]);

  // Filter by search
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return grouped;
    const q = searchQuery.toLowerCase();
    return {
      dms: grouped.dms.filter(c => (c.display_name || c.name || '').toLowerCase().includes(q)),
      publicChannels: grouped.publicChannels.filter(c => (c.display_name || c.name || '').toLowerCase().includes(q)),
      projectChannels: grouped.projectChannels.filter(c => (c.display_name || c.name || '').toLowerCase().includes(q)),
    };
  }, [grouped, searchQuery]);

  const SectionHeader = ({ title, section, count, onAdd }) => (
    <div className="flex items-center justify-between px-3 py-1.5 mt-2">
      <button
        onClick={() => toggleSection(section)}
        className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-300 transition-colors"
      >
        <ChevronDown className={cn(
          'w-3 h-3 transition-transform',
          !expandedSections[section] && '-rotate-90'
        )} />
        {title}
        <span className="text-zinc-600 font-normal ml-1">{count}</span>
      </button>
      {onAdd && (
        <button
          onClick={onAdd}
          className="p-0.5 rounded hover:bg-zinc-800 text-zinc-600 hover:text-zinc-300 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <div className={cn('flex flex-col h-full bg-zinc-950 border-r border-zinc-800/50', className)}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800/50">
        <h2 className="text-base font-bold text-white mb-2.5">Messages</h2>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-zinc-900/50 border border-zinc-800/50 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-purple-500/30 focus:border-purple-500/30"
          />
        </div>
      </div>

      {/* Channel list */}
      <div className="flex-1 overflow-y-auto py-1 scrollbar-thin">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* Direct Messages */}
            <SectionHeader title="Direct Messages" section="dms" count={filtered.dms.length} onAdd={onCreateDM} />
            {expandedSections.dms && (
              <div className="px-1.5 space-y-0.5">
                {filtered.dms.map(ch => (
                  <ChannelItem
                    key={ch.id}
                    channel={ch}
                    isActive={ch.id === activeChannelId}
                    onClick={onSelectChannel}
                    currentUserId={currentUserId}
                  />
                ))}
                {filtered.dms.length === 0 && (
                  <p className="text-xs text-zinc-600 px-3 py-2">No direct messages yet</p>
                )}
              </div>
            )}

            {/* Public Channels */}
            <SectionHeader title="Channels" section="channels" count={filtered.publicChannels.length} onAdd={onCreateChannel} />
            {expandedSections.channels && (
              <div className="px-1.5 space-y-0.5">
                {filtered.publicChannels.map(ch => (
                  <ChannelItem
                    key={ch.id}
                    channel={ch}
                    isActive={ch.id === activeChannelId}
                    onClick={onSelectChannel}
                    currentUserId={currentUserId}
                  />
                ))}
                {filtered.publicChannels.length === 0 && (
                  <p className="text-xs text-zinc-600 px-3 py-2">No channels yet</p>
                )}
              </div>
            )}

            {/* Project Channels */}
            {filtered.projectChannels.length > 0 && (
              <>
                <SectionHeader title="Projects" section="projects" count={filtered.projectChannels.length} />
                {expandedSections.projects && (
                  <div className="px-1.5 space-y-0.5">
                    {filtered.projectChannels.map(ch => (
                      <ChannelItem
                        key={ch.id}
                        channel={ch}
                        isActive={ch.id === activeChannelId}
                        onClick={onSelectChannel}
                        currentUserId={currentUserId}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MESSAGE BUBBLE
// ============================================================================

const MessageBubble = ({ message, isGrouped, onReply, onReact, onEdit, onDelete, onPin, currentUserId }) => {
  const [showActions, setShowActions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const isOwn = message.user_id === currentUserId;

  return (
    <div
      className={cn(
        'group relative px-4',
        isGrouped ? 'py-0.5' : 'py-1.5',
        'hover:bg-zinc-800/20 transition-colors'
      )}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => { setShowActions(false); setShowEmojiPicker(false); }}
    >
      <div className="flex items-start gap-3 max-w-3xl">
        {/* Avatar — hidden if grouped */}
        {isGrouped ? (
          <div className="w-8 flex-shrink-0">
            {/* Hover timestamp for grouped messages */}
            <span className="hidden group-hover:inline text-[10px] text-zinc-600 tabular-nums leading-8">
              {formatMessageTime(message.created_at)}
            </span>
          </div>
        ) : (
          <MiniAvatar username={message.username} size={32} className="mt-0.5" />
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header — hidden if grouped */}
          {!isGrouped && (
            <div className="flex items-baseline gap-2 mb-0.5">
              <span className="text-sm font-semibold text-white">{message.username}</span>
              <span className="text-[11px] text-zinc-600 tabular-nums">
                {formatMessageTime(message.created_at)}
              </span>
              {message.edited_at && (
                <span className="text-[10px] text-zinc-600">(edited)</span>
              )}
              {message.is_pinned && (
                <Pin className="w-3 h-3 text-amber-500" />
              )}
            </div>
          )}

          {/* Message body */}
          <div className={cn(
            'text-[13.5px] leading-relaxed',
            message.is_deleted ? 'text-zinc-600 italic' : 'text-zinc-300'
          )}>
            {message.content_type === 'code' ? (
              <pre className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 text-xs font-mono text-zinc-300 overflow-x-auto">
                <code>{message.content}</code>
              </pre>
            ) : (
              <p className="whitespace-pre-wrap break-words">{message.content}</p>
            )}
          </div>

          {/* Attachments */}
          {message.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1.5">
              {message.attachments.map((att, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-900/50 border border-zinc-800/50 rounded-lg text-xs text-zinc-400">
                  <Paperclip className="w-3 h-3" />
                  <span className="truncate max-w-[150px]">{att.name || 'attachment'}</span>
                </div>
              ))}
            </div>
          )}

          {/* Reactions */}
          {message.reactions?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {message.reactions.map((reaction, i) => (
                <button
                  key={i}
                  onClick={() => onReact(message.id, reaction.emoji)}
                  className={cn(
                    'flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs border transition-colors',
                    reaction.users?.includes(currentUserId)
                      ? 'bg-purple-500/10 border-purple-500/30 text-purple-300'
                      : 'bg-zinc-800/50 border-zinc-700/30 text-zinc-400 hover:border-zinc-600'
                  )}
                >
                  <span>{reaction.emoji}</span>
                  <span className="text-[10px] font-medium">{reaction.count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Thread indicator */}
          {message.thread_reply_count > 0 && (
            <button
              onClick={() => onReply(message)}
              className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-md text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 transition-colors"
            >
              <MessageSquare className="w-3 h-3" />
              {message.thread_reply_count} {message.thread_reply_count === 1 ? 'reply' : 'replies'}
            </button>
          )}
        </div>
      </div>

      {/* Hover action bar */}
      {showActions && !message.is_deleted && (
        <div className="absolute right-4 -top-3 flex items-center bg-zinc-900 border border-zinc-800 rounded-lg shadow-lg overflow-hidden z-10">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="p-1.5 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
            title="React"
          >
            <Smile className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onReply(message)}
            className="p-1.5 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
            title="Reply in thread"
          >
            <Reply className="w-3.5 h-3.5" />
          </button>
          {isOwn && (
            <button
              onClick={() => onEdit(message)}
              className="p-1.5 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
              title="Edit"
            >
              <Edit3 className="w-3.5 h-3.5" />
            </button>
          )}
          {isOwn && (
            <button
              onClick={() => onDelete(message.id)}
              className="p-1.5 hover:bg-red-500/10 text-zinc-400 hover:text-red-400 transition-colors"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Quick emoji picker */}
      {showEmojiPicker && (
        <div className="absolute right-4 top-6 flex items-center gap-0.5 bg-zinc-900 border border-zinc-800 rounded-lg shadow-lg px-1 py-0.5 z-20">
          {EMOJI_QUICK.map(emoji => (
            <button
              key={emoji}
              onClick={() => { onReact(message.id, emoji); setShowEmojiPicker(false); }}
              className="p-1 hover:bg-zinc-800 rounded transition-colors text-base"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DATE DIVIDER
// ============================================================================

const DateDivider = ({ date }) => (
  <div className="flex items-center gap-3 px-4 py-2 my-1">
    <div className="flex-1 h-px bg-zinc-800/50" />
    <span className="text-[11px] font-medium text-zinc-500 flex-shrink-0">
      {formatDateDivider(date)}
    </span>
    <div className="flex-1 h-px bg-zinc-800/50" />
  </div>
);

// ============================================================================
// MESSAGE COMPOSER
// ============================================================================

const MessageComposer = ({ channelId, channelName, onSend, replyTo, onClearReply, disabled }) => {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef(null);

  // Focus on mount or when reply changes
  useEffect(() => {
    textareaRef.current?.focus();
  }, [channelId, replyTo]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    }
  }, [content]);

  const handleSend = async () => {
    const trimmed = content.trim();
    if (!trimmed || sending) return;

    setSending(true);
    try {
      await onSend(trimmed, replyTo?.id);
      setContent('');
      if (onClearReply) onClearReply();
    } catch (e) {
      console.error('Failed to send message:', e);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-zinc-800/50 bg-zinc-950/80 backdrop-blur-sm px-4 py-3">
      {/* Reply preview */}
      {replyTo && (
        <div className="flex items-center gap-2 mb-2 pl-3 border-l-2 border-purple-500">
          <div className="flex-1 min-w-0">
            <span className="text-[11px] text-purple-400 font-medium">
              Replying to {replyTo.username}
            </span>
            <p className="text-xs text-zinc-500 truncate">{replyTo.content}</p>
          </div>
          <button
            onClick={onClearReply}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-500"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={replyTo ? 'Reply...' : `Message ${channelName || 'channel'}...`}
            disabled={disabled}
            rows={1}
            className={cn(
              'w-full bg-zinc-900/60 border border-zinc-800/50 rounded-xl px-4 py-2.5 pr-10',
              'text-sm text-white placeholder:text-zinc-600',
              'focus:outline-none focus:ring-1 focus:ring-purple-500/30 focus:border-purple-500/30',
              'resize-none transition-all max-h-[160px]',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          />
        </div>

        <button
          onClick={handleSend}
          disabled={!content.trim() || sending || disabled}
          className={cn(
            'p-2.5 rounded-xl transition-all duration-150',
            content.trim()
              ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-600/20'
              : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
          )}
        >
          {sending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </div>

      <p className="text-[10px] text-zinc-600 mt-1.5 px-1">
        <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-[9px]">Enter</kbd> to send · <kbd className="px-1 py-0.5 bg-zinc-800 rounded text-[9px]">Shift+Enter</kbd> for new line
      </p>
    </div>
  );
};

// ============================================================================
// CHANNEL HEADER
// ============================================================================

const ChannelHeader = ({ channel, onBack, onShowMembers, memberCount }) => {
  if (!channel) return null;

  const Icon = CHANNEL_ICONS[channel.channel_type] || Hash;
  const isDM = channel.channel_type === 'direct_message';

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-800/50 bg-zinc-950/90 backdrop-blur-sm">
      {/* Back button — mobile only */}
      <button
        onClick={onBack}
        className="md:hidden p-1 -ml-1 rounded-lg hover:bg-zinc-800 text-zinc-400"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      {isDM ? (
        <MiniAvatar
          src={channel.dm_user?.profilePictureUrl}
          username={channel.dm_user?.username || channel.display_name}
          size={28}
        />
      ) : (
        <Icon className="w-4.5 h-4.5 text-zinc-400" />
      )}

      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-white truncate">
          {channel.display_name || channel.name}
        </h3>
        {channel.description && (
          <p className="text-[11px] text-zinc-500 truncate">{channel.description}</p>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={onShowMembers}
          className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
        >
          <Users className="w-3.5 h-3.5" />
          <span>{memberCount || channel.member_count || 0}</span>
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// NEW CHANNEL MODAL
// ============================================================================

const NewChannelModal = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      await onCreate({
        name: name.trim().toLowerCase().replace(/\s+/g, '-'),
        display_name: name.trim(),
        description: description.trim(),
        channel_type: isPrivate ? 'private' : 'public',
      });
      onClose();
    } catch (e) {
      console.error('Failed to create channel:', e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h3 className="text-base font-semibold text-white">Create Channel</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-zinc-800 text-zinc-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">Channel Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. web-dev-team"
              className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-purple-500/30"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">Description (optional)</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this channel about?"
              className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-purple-500/30"
            />
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <div className={cn(
              'w-9 h-5 rounded-full transition-colors relative',
              isPrivate ? 'bg-purple-600' : 'bg-zinc-700'
            )}>
              <div className={cn(
                'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
                isPrivate ? 'translate-x-4' : 'translate-x-0.5'
              )} />
            </div>
            <div>
              <span className="text-sm text-zinc-300">Private channel</span>
              <p className="text-[11px] text-zinc-500">Only invited members can see and join</p>
            </div>
          </label>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-800 bg-zinc-900/50">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || creating}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {creating ? 'Creating...' : 'Create Channel'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// NEW DM MODAL
// ============================================================================

const NewDMModal = ({ onClose, onCreateDM }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!searchQuery.trim()) { setResults([]); return; }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/users/search', { params: { q: searchQuery, limit: 10 } });
        setResults(res.data.users || res.data || []);
      } catch (e) {
        console.error('User search failed:', e);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelect = async (user) => {
    setCreating(true);
    try {
      await onCreateDM(user.id);
      onClose();
    } catch (e) {
      console.error('Failed to create DM:', e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h3 className="text-base font-semibold text-white">New Message</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-zinc-800 text-zinc-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search people..."
              className="w-full pl-9 pr-3 py-2.5 bg-zinc-800/50 border border-zinc-700/50 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-purple-500/30"
              autoFocus
            />
          </div>

          <div className="mt-3 max-h-[280px] overflow-y-auto space-y-0.5">
            {searching && (
              <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
              </div>
            )}
            {!searching && results.map(user => (
              <button
                key={user.id}
                onClick={() => handleSelect(user)}
                disabled={creating}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-zinc-800/50 text-left transition-colors"
              >
                <MiniAvatar
                  src={user.profile_picture_url || user.profilePictureUrl}
                  username={user.username}
                  size={32}
                />
                <div>
                  <p className="text-sm text-white font-medium">{user.username}</p>
                  {user.department && (
                    <p className="text-[11px] text-zinc-500">{user.department}</p>
                  )}
                </div>
              </button>
            ))}
            {!searching && searchQuery && results.length === 0 && (
              <p className="text-sm text-zinc-500 text-center py-4">No users found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// EMPTY CONVERSATION STATE
// ============================================================================

const EmptyConversation = () => (
  <div className="flex-1 flex flex-col items-center justify-center px-6">
    <div className="w-16 h-16 rounded-2xl bg-zinc-800/30 flex items-center justify-center mb-4 ring-1 ring-zinc-800/50">
      <MessageCircle className="w-7 h-7 text-zinc-600" />
    </div>
    <h3 className="text-base font-semibold text-zinc-300 mb-1.5">Select a conversation</h3>
    <p className="text-sm text-zinc-500 text-center max-w-xs leading-relaxed">
      Choose a channel or direct message from the sidebar, or start a new conversation.
    </p>
  </div>
);

// ============================================================================
// WELCOME STATE (new channel, no messages)
// ============================================================================

const ChannelWelcome = ({ channel }) => {
  const Icon = CHANNEL_ICONS[channel?.channel_type] || Hash;
  return (
    <div className="px-4 py-8">
      <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-3 ring-1 ring-purple-500/20">
        <Icon className="w-6 h-6 text-purple-400" />
      </div>
      <h2 className="text-xl font-bold text-white mb-1">
        {channel?.display_name || channel?.name}
      </h2>
      {channel?.description && (
        <p className="text-sm text-zinc-400 mb-2">{channel.description}</p>
      )}
      <p className="text-sm text-zinc-500">
        This is the very beginning of <span className="font-medium text-zinc-300">{channel?.display_name || channel?.name}</span>. Say something!
      </p>
    </div>
  );
};

// ============================================================================
// MESSAGES PAGE (main export)
// ============================================================================

const MessagesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [showNewDM, setShowNewDM] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollRef = useRef(null);

  // ---- Fetch channels ----
  const fetchChannels = useCallback(async () => {
    try {
      const res = await api.get('/messaging/channels');
      setChannels(res.data.channels || []);
    } catch (e) {
      console.error('Failed to load channels:', e);
    } finally {
      setLoadingChannels(false);
    }
  }, []);

  // ---- Fetch messages for active channel ----
  const fetchMessages = useCallback(async (channelId, beforeId = null) => {
    if (!channelId) return;
    if (!beforeId) setLoadingMessages(true);

    try {
      const params = { limit: 50 };
      if (beforeId) params.before = beforeId;

      const res = await api.get(`/messaging/channels/${channelId}/messages`, { params });
      const newMessages = res.data.messages || [];

      if (beforeId) {
        setMessages(prev => [...newMessages, ...prev]);
      } else {
        setMessages(newMessages);
      }
      setHasMoreMessages(res.data.has_more || false);
    } catch (e) {
      console.error('Failed to load messages:', e);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // ---- Poll for new messages every 5 seconds ----
  useEffect(() => {
    if (!activeChannel?.id) return;

    const poll = async () => {
      try {
        const lastId = messages.length > 0 ? messages[messages.length - 1].id : null;
        if (!lastId) return;

        const res = await api.get(`/messaging/channels/${activeChannel.id}/messages`, {
          params: { after: lastId, limit: 50 }
        });
        const newMsgs = res.data.messages || [];
        if (newMsgs.length > 0) {
          setMessages(prev => [...prev, ...newMsgs]);
        }
      } catch (e) {
        // Silent fail
      }
    };

    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeChannel?.id, messages]);

  // ---- Initial load ----
  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // ---- Load messages when channel changes ----
  useEffect(() => {
    if (activeChannel?.id) {
      fetchMessages(activeChannel.id);
    }
  }, [activeChannel?.id, fetchMessages]);

  // ---- Auto-scroll to bottom on new messages ----
  useEffect(() => {
    if (messagesEndRef.current && !loadingMessages) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, loadingMessages]);

  // ---- Handle channel from URL ----
  useEffect(() => {
    const channelId = searchParams.get('channel');
    if (channelId && channels.length > 0) {
      const ch = channels.find(c => c.id === parseInt(channelId));
      if (ch) {
        setActiveChannel(ch);
        setMobileShowChat(true);
      }
    }
  }, [searchParams, channels]);

  // ---- Select channel ----
  const handleSelectChannel = (channel) => {
    setActiveChannel(channel);
    setMobileShowChat(true);
    setReplyTo(null);
    setEditingMessage(null);
  };

  // ---- Send message ----
  const handleSendMessage = async (content, parentMessageId = null) => {
    if (!activeChannel?.id) return;

    const res = await api.post(`/messaging/channels/${activeChannel.id}/messages`, {
      content,
      parent_message_id: parentMessageId,
    });

    const newMsg = res.data.data;
    if (newMsg) {
      setMessages(prev => [...prev, newMsg]);
    }
  };

  // ---- React to message ----
  const handleReact = async (messageId, emoji) => {
    try {
      await api.post(`/messaging/messages/${messageId}/reactions`, { emoji });
      // Refresh messages to get updated reactions
      if (activeChannel?.id) fetchMessages(activeChannel.id);
    } catch (e) {
      console.error('Failed to react:', e);
    }
  };

  // ---- Delete message ----
  const handleDelete = async (messageId) => {
    try {
      await api.delete(`/messaging/messages/${messageId}`);
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, is_deleted: true, content: '[deleted]' } : m
      ));
    } catch (e) {
      console.error('Failed to delete:', e);
    }
  };

  // ---- Edit message ----
  const handleEdit = (message) => {
    setEditingMessage(message);
    // For now, just log — full edit UI would be more complex
    // In production, this would swap the message into an editable state
  };

  // ---- Pin message ----
  const handlePin = async (messageId) => {
    try {
      await api.post(`/messaging/messages/${messageId}/pin`);
      if (activeChannel?.id) fetchMessages(activeChannel.id);
    } catch (e) {
      console.error('Failed to pin:', e);
    }
  };

  // ---- Create channel ----
  const handleCreateChannel = async (data) => {
    const res = await api.post('/messaging/channels', data);
    const newChannel = res.data.channel;
    setChannels(prev => [newChannel, ...prev]);
    setActiveChannel(newChannel);
    setMobileShowChat(true);
  };

  // ---- Create DM ----
  const handleCreateDM = async (userId) => {
    const res = await api.post('/messaging/dm/create', { user_id: userId });
    const channel = res.data.channel;
    // Add to channels if not already there
    setChannels(prev => {
      const exists = prev.find(c => c.id === channel.id);
      if (exists) return prev;
      return [channel, ...prev];
    });
    setActiveChannel(channel);
    setMobileShowChat(true);
  };

  // ---- Load more (scroll up) ----
  const handleLoadMore = () => {
    if (messages.length > 0 && hasMoreMessages) {
      fetchMessages(activeChannel.id, messages[0].id);
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] md:h-[calc(100vh-4rem)] flex bg-zinc-950">
      {/* ---- Channel Sidebar ---- */}
      <ChannelSidebar
        channels={channels}
        activeChannelId={activeChannel?.id}
        onSelectChannel={handleSelectChannel}
        onCreateChannel={() => setShowNewChannel(true)}
        onCreateDM={() => setShowNewDM(true)}
        currentUserId={user?.id}
        loading={loadingChannels}
        className={cn(
          'w-72 flex-shrink-0',
          // Mobile: show sidebar when no chat selected
          mobileShowChat ? 'hidden md:flex' : 'flex w-full md:w-72'
        )}
      />

      {/* ---- Conversation Panel ---- */}
      <div className={cn(
        'flex-1 flex flex-col min-w-0',
        // Mobile: show when chat selected
        !mobileShowChat ? 'hidden md:flex' : 'flex'
      )}>
        {!activeChannel ? (
          <EmptyConversation />
        ) : (
          <>
            {/* Channel header */}
            <ChannelHeader
              channel={activeChannel}
              onBack={() => setMobileShowChat(false)}
              memberCount={activeChannel.member_count}
            />

            {/* Messages area */}
            <div
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto"
            >
              {/* Load more */}
              {hasMoreMessages && (
                <div className="flex justify-center py-3">
                  <button
                    onClick={handleLoadMore}
                    className="text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors"
                  >
                    Load earlier messages
                  </button>
                </div>
              )}

              {loadingMessages ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <ChannelWelcome channel={activeChannel} />
              ) : (
                <div className="py-2">
                  {messages.map((msg, i) => {
                    const prevMsg = i > 0 ? messages[i - 1] : null;
                    const showDate = shouldShowDateDivider(msg, prevMsg);
                    const isGrouped = !showDate && shouldGroupMessages(msg, prevMsg);

                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && <DateDivider date={msg.created_at} />}
                        <MessageBubble
                          message={msg}
                          isGrouped={isGrouped}
                          onReply={(m) => setReplyTo(m)}
                          onReact={handleReact}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onPin={handlePin}
                          currentUserId={user?.id}
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <MessageComposer
              channelId={activeChannel.id}
              channelName={activeChannel.display_name || activeChannel.name}
              onSend={handleSendMessage}
              replyTo={replyTo}
              onClearReply={() => setReplyTo(null)}
            />
          </>
        )}
      </div>

      {/* ---- Modals ---- */}
      {showNewChannel && (
        <NewChannelModal
          onClose={() => setShowNewChannel(false)}
          onCreate={handleCreateChannel}
        />
      )}
      {showNewDM && (
        <NewDMModal
          onClose={() => setShowNewDM(false)}
          onCreateDM={handleCreateDM}
        />
      )}
    </div>
  );
};

export default MessagesPage;